// ========================================
// User Profile View
// ========================================

const ProfileView = {
    // ─── Mi perfil, design B2 (Oct 2026): a cinema header with the person's figures for their
    // role, their usual places with what is pending, and their data and security side by side.
    // Everything optional is guarded: the view also renders with only AuthManager available.
    ROLE_LABELS: { 'admin': 'Administrador', 'coordinator': 'Coordinador académico', 'rrhh': 'Recursos humanos', 'professor': 'Profesor', 'student': 'Alumno', 'warehouse': 'Logística y almacén' },

    _get(name) { return typeof globalThis[name] !== 'undefined' ? globalThis[name] : (typeof window !== 'undefined' ? window[name] : undefined); },
    _all(key) { const DM = this._get('DataManager'); try { return DM ? DM.getAll(DM.KEYS[key]) || [] : []; } catch { return []; } },
    _iso(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; },
    _week() {
        const now = new Date(), monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7));
        return Array.from({ length: 7 }, (_, i) => new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + i));
    },
    // Class sessions of this week that match `keep(cls, subject)` (single dates or weekly slots).
    _weekSessions(keep) {
        const DM = this._get('DataManager');
        if (!DM || !DM.getClasses) return [];
        const days = this._week().map(d => ({ iso: this._iso(d), dow: d.getDay() }));
        const out = [];
        (DM.getClasses() || []).forEach(cls => {
            if (!cls.startDate) return;
            const subject = DM.getSubject ? DM.getSubject(cls.subjectId) : null;
            if (!keep(cls, subject)) return;
            const end = cls.endDate && cls.endDate > cls.startDate ? cls.endDate : cls.startDate;
            days.forEach(d => {
                if (d.iso < cls.startDate || d.iso > end) return;
                if (cls.startDate === end ? d.iso === cls.startDate : Number(cls.dayOfWeek) === d.dow) out.push({ cls, subject, date: d.iso });
            });
        });
        return out;
    },
    _hours(sessions) {
        const min = t => { const [h, m] = String(t || '').split(':').map(Number); return Number.isFinite(h) ? h * 60 + (m || 0) : null; };
        const total = sessions.reduce((n, s) => { const a = min(s.cls.startTime), b = min(s.cls.endTime); return n + (a !== null && b !== null && b > a ? b - a : 0); }, 0);
        return Math.round(total / 6) / 10;
    },

    // The figures in the header: real data of the person's role only.
    _stats(user) {
        const DM = this._get('DataManager');
        const stat = (n, label, route) => ({ n, label, route });
        if (user.role === 'admin' || user.role === 'coordinator') {
            const FS = this._get('FileStorage');
            return [stat(this._all('PROFESSORS').length, 'Profesores', 'profesores'), stat(this._all('STUDENTS').length, 'Alumnos', 'alumnado'),
                stat(this._all('FORMATIONS').length, 'Formaciones', 'formaciones'), stat(FS?.dataStore?.leadsSummary?.total || 0, 'Contactos', 'leads')];
        }
        if (user.role === 'rrhh') {
            const SP = this._get('StaffPay');
            const people = this._all('PROFESSORS');
            const today = this._iso(new Date()), month = today.slice(0, 7);
            const contracts = SP ? people.flatMap(p => SP.contractsOf(p)) : [];
            return [stat(people.length, 'Profesores', 'profesores'),
                stat(contracts.filter(c => c.startDate && c.startDate.slice(0, 7) === month).length, 'Altas este mes', 'planificacion-mensual'),
                stat(contracts.filter(c => !c.freelance && !c.advisoryNotified && (!c.endDate || c.endDate >= today)).length, 'Sin avisar a la asesoría', 'profesores')];
        }
        if (user.professorId) {
            const mine = this._weekSessions((cls, subject) => String(cls.professorId || subject?.professorId || '') === String(user.professorId));
            const subjects = DM?.getSubjects ? DM.getSubjects().filter(s => String(s.professorId) === String(user.professorId)) : [];
            return [stat(mine.length, 'Clases esta semana', 'calendario-formacion'), stat(this._hours(mine).toLocaleString('es-ES'), 'Horas esta semana', 'calendario-formacion'),
                stat(new Set(subjects.map(s => (s.name || '').trim().toLowerCase())).size, 'Asignaturas', 'calendario-formacion'),
                stat(new Set(subjects.map(s => s.formationId).filter(Boolean)).size, 'Formaciones', 'calendario-formacion')];
        }
        if (user.role === 'student') {
            const SS = this._get('StudentService');
            const me = SS ? (SS.getStudents() || []).find(s => String(s.id) === String(user.studentId)) : null;
            const forms = (me?.formations || []).map(String);
            const sessions = this._weekSessions(cls => forms.includes(String(cls.formationId)));
            const subjects = DM?.getSubjects ? DM.getSubjects().filter(s => forms.includes(String(s.formationId))) : [];
            return forms.length ? [stat(sessions.length, 'Clases esta semana', 'calendario-formacion'), stat(this._hours(sessions).toLocaleString('es-ES'), 'Horas esta semana', 'calendario-formacion'),
                stat(new Set(subjects.map(s => (s.name || '').trim().toLowerCase())).size, 'Asignaturas', 'calendario-formacion')] : [];
        }
        if (user.role === 'warehouse') {
            const IS = this._get('InventoryService');
            const items = IS ? IS.getAllItems() || [] : [];
            return items.length ? [stat(items.length, 'Elementos', 'almacen'), stat(items.reduce((n, i) => n + Math.max(0, Number(i.totalQuantity ?? i.quantity ?? 0) - Number(i.quantity ?? 0)), 0), 'Unidades prestadas', 'almacen')] : [];
        }
        return [];
    },

    // The person's usual places, with what is pending; only those their role can open.
    _tiles(user) {
        const DM = this._get('DataManager');
        const unread = DM?.getUnreadCount ? DM.getUnreadCount(user.username) || 0 : 0;
        const T = (route, icon, tone, name, sub, badge = '') => ({ route, icon, tone, name, sub, badge });
        const msg = T('mensajes', 'fa-envelope', 'teal', 'Mensajes', unread ? `${unread} sin leer` : 'Tu bandeja de entrada', unread ? String(unread) : '');
        const byRole = {
            admin: [T('acciones-pendientes', 'fa-list-check', 'amber', 'Acciones pendientes', 'Lo que espera tu firma o revisión'), msg, T('settings', 'fa-sliders', 'grey', 'Configuración global', 'Centro, festivos y actualización'), T('permisos-roles', 'fa-user-shield', 'grey', 'Permisos por rol', 'Qué ve cada rol en el menú')],
            coordinator: [msg, T('calendario-formacion', 'fa-calendar-week', 'teal', 'Programación lectiva', 'Horarios de las formaciones'), T('calendario', 'fa-calendar-days', 'amber', 'Calendario general', 'Todo el curso de un vistazo'), T('formaciones', 'fa-graduation-cap', 'grey', 'Formaciones', 'Cursos, grupos y alumnado')],
            rrhh: [msg, T('profesores', 'fa-users', 'teal', 'Profesores', 'Fichas, contratos y pagos'), T('nominas', 'fa-file-invoice-dollar', 'amber', 'Nóminas', 'Subir y revisar nóminas'), T('planificacion-mensual', 'fa-calendar-check', 'grey', 'Planificación mensual', 'Altas, bajas y días de clase')],
            professor: [T('calendario-formacion', 'fa-calendar-week', 'teal', 'Mi horario', 'Tus clases de la semana'), T('nominas', 'fa-file-invoice-dollar', 'amber', 'Mis nóminas', 'Tus nóminas y certificados'), T('justificantes', 'fa-file-medical', 'red', 'Justificantes', 'Sube o consulta un justificante'), msg],
            student: [T('calendario-formacion', 'fa-calendar-week', 'teal', 'Mi horario', 'Las clases de tu curso'), T('tareas', 'fa-pen-to-square', 'amber', 'Mis tareas', 'Entregas y correcciones'), T('almacen', 'fa-camera', 'grey', 'Pedir material', 'Cámaras, luces y sonido'), T('calificaciones', 'fa-star', 'grey', 'Mis calificaciones', 'Notas publicadas')],
            warehouse: [T('almacen', 'fa-boxes-stacked', 'teal', 'Almacén', 'Inventario y préstamos'), msg, T('comunidad', 'fa-comments', 'grey', 'Comunidad', 'Canales de la escuela'), T('buscar', 'fa-magnifying-glass', 'grey', 'Buscador', 'Encuentra cualquier cosa')]
        };
        const role = user.professorId && !['admin', 'coordinator', 'rrhh'].includes(user.role) ? 'professor' : user.role;
        const RP = this._get('RolePermissions');
        return (byRole[role] || byRole.student).filter(t => !RP || !RP.canView || RP.canView(t.route) || t.route === 'mensajes');
    },

    _since(user) {
        const d = user.createdAt ? new Date(user.createdAt) : null;
        return d && !Number.isNaN(d.getTime()) ? `En SICAF desde ${d.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })}` : '';
    },

    render() {
        const user = AuthManager.getCurrentUser();
        if (!user) return '<div class="card"><p style="text-align: center;">Usuario no autenticado. Por favor, inicia sesión.</p></div>';
        const esc = v => Utils.sanitizeHTML(String(v ?? ''));
        const roleLabel = this.ROLE_LABELS[user.role] || user.role;
        const DM = this._get('DataManager');
        let photo = user.photo;
        if (!photo && user.professorId && DM?.getProfessor) photo = DM.getProfessor(user.professorId)?.photo;
        const avatar = photo && typeof StudentPhotos !== 'undefined' ? `<img ${StudentPhotos.attributes(photo)} alt="" id="profile-avatar-img">` : esc(String(user.name || '?').charAt(0).toUpperCase());
        const stats = this._stats(user), tiles = this._tiles(user), since = this._since(user);
        const go = route => `${UIEvents.attr("click", function(event,captured){App.navigate(String(captured[0]));}, [route])}`;
        const ficha = user.professorId ? { label: ['admin', 'coordinator', 'rrhh'].includes(user.role) ? 'Ficha de personal' : 'Mi ficha docente', sub: 'Asignaturas, horario, contratos y documentos', route: 'profesor/' + user.professorId } : null;
        const portal = { admin: 'Tus accesos de Administración', coordinator: 'Tus accesos de Coordinación', rrhh: 'Tus accesos de RRHH', student: 'Tu portal de alumno', warehouse: 'Tus accesos del almacén' }[user.role] || 'Tu portal docente';
        return `
            <div class="pfx dnr-proposal" style="padding-top: 1rem;">
                <section class="pfx-hero" aria-label="Mi perfil">
                    <span class="pfx-sprocket" aria-hidden="true"></span>
                    <div class="pfx-hero-grid">
                        <div class="pfx-avatar-wrap">
                            <div id="profile-avatar-display" class="pfx-avatar">${avatar}</div>
                            <label for="profile-upload-avatar" class="pfx-avatar-btn" title="Cambiar foto"><i class="fas fa-camera" aria-hidden="true"></i><span class="sr-only">Cambiar foto</span>
                                <input type="file" id="profile-upload-avatar" class="pfx-hidden" accept="image/png,image/jpeg,image/gif,image/webp" ${UIEvents.attr("change", function(event,captured){ProfileView.handleAvatarUpload(event);}, [])}>
                            </label>
                        </div>
                        <div class="pfx-who">
                            <span class="pfx-eyebrow">Mi perfil</span>
                            <h1 class="pfx-name">${esc(user.name)}</h1>
                            <div class="pfx-chips">
                                <span class="pfx-chip is-role">${esc(roleLabel)}</span>
                                <span class="pfx-chip">@${esc(user.username)}</span>
                                <span class="pfx-chip" id="profile-hero-email" hidden></span>
                                ${since ? `<span class="pfx-chip">${esc(since)}</span>` : ''}
                            </div>
                        </div>
                        ${stats.length ? `<div class="pfx-stats" id="profile-stats">${stats.map(s => `<button type="button" class="pfx-stat" ${go(s.route)}><b>${esc(s.n)}</b><span>${esc(s.label)}</span></button>`).join('')}</div>` : ''}
                    </div>
                </section>

                ${tiles.length ? `<section class="pfx-card">
                    <div class="pfx-card-head"><span class="pfx-ico is-amber" aria-hidden="true"><i class="fas fa-bolt"></i></span><h2>${esc(portal)}</h2><span class="pfx-muted">Lo que más usas, con lo que tienes pendiente</span></div>
                    <div class="pfx-tiles">${tiles.map(t => `<button type="button" class="pfx-tile" ${go(t.route)}>
                        <span class="pfx-ico is-${t.tone}" aria-hidden="true"><i class="fas ${t.icon}"></i></span><b>${esc(t.name)}</b><span>${esc(t.sub)}</span>
                        ${t.badge ? `<span class="pfx-badge">${esc(t.badge)}</span>` : ''}</button>`).join('')}</div>
                </section>` : ''}

                <div class="pfx-columns">
                    <section class="pfx-card">
                        <div class="pfx-card-head"><span class="pfx-ico is-teal" aria-hidden="true"><i class="fas fa-id-card"></i></span><h2>Datos personales</h2></div>
                        <div class="pfx-body">
                            <form id="profile-details-form" class="pfx-form" ${UIEvents.attr("submit", function(event,captured){ProfileView.handleUpdateDetails(event);}, [])}>
                                <div class="pfx-grid2">
                                    <label class="pfx-field" for="profile-input-name"><span>Nombre completo</span><input type="text" id="profile-input-name" class="pfx-input" value="${esc(user.name)}" required></label>
                                    <label class="pfx-field" for="profile-input-username"><span>Usuario</span><input type="text" id="profile-input-username" class="pfx-input" value="${esc(user.username)}" required><small>Con él entras en SICAF.</small></label>
                                </div>
                                <button type="submit" class="pfx-btn"><i class="fas fa-save" aria-hidden="true"></i> Guardar datos</button>
                            </form>
                            <div class="pfx-row profile-account-email">
                                <div class="pfx-row-main"><span class="pfx-label">Correo electrónico</span><b id="profile-email-current">Cargando correo…</b><small>Aquí te llegan los avisos y la recuperación de contraseña.</small></div>
                                <details class="pfx-details">
                                    <summary class="pfx-ghost">Cambiar correo electrónico</summary>
                                    <form id="profile-email-form" class="pfx-form" ${UIEvents.attr("submit", function(event,captured){ProfileView.handleAccountEmail(event);}, [])}>
                                        <label class="pfx-field" for="profile-email-input"><span>Nuevo correo</span><input class="pfx-input" id="profile-email-input" type="email" autocomplete="email" maxlength="254" required disabled></label>
                                        <label class="pfx-field" for="profile-email-password"><span>Contraseña actual</span><input class="pfx-input" id="profile-email-password" type="password" autocomplete="current-password" required></label>
                                        <small class="pfx-muted">El cambio actualizará el correo de tu ficha y cerrará las demás sesiones de tu cuenta.</small>
                                        <button class="pfx-btn" type="submit" disabled>Guardar correo</button>
                                    </form>
                                </details>
                            </div>
                            <p id="profile-email-feedback" class="pfx-feedback" role="status" aria-live="polite"></p>
                            <button class="pfx-ghost" type="button" id="profile-email-retry" hidden ${UIEvents.attr("click", function(event,captured){ProfileView.init();}, [])}>Reintentar</button>
                            ${ficha ? `<div class="pfx-row"><div class="pfx-row-main"><span class="pfx-label">${esc(ficha.label)}</span><b>${esc(ficha.sub)}</b></div><button type="button" class="pfx-ghost" ${go(ficha.route)}>Abrir</button></div>` : ''}
                        </div>
                    </section>

                    <section class="pfx-card">
                        <div class="pfx-card-head"><span class="pfx-ico is-red" aria-hidden="true"><i class="fas fa-key"></i></span><h2>Seguridad</h2></div>
                        <div class="pfx-body">
                            <form id="profile-password-form" class="pfx-form" ${UIEvents.attr("submit", function(event,captured){ProfileView.handleChangePassword(event);}, [])}>
                                <label class="pfx-field" for="profile-input-current-password"><span>Contraseña actual</span>
                                    <input type="password" id="profile-input-current-password" class="pfx-input" autocomplete="current-password" required></label>
                                <p id="profile-password-hint" class="pfx-muted">Para proteger tu cuenta, confirma la contraseña que usas ahora.</p>
                                <label class="pfx-field" for="profile-input-password"><span>Nueva contraseña</span>
                                    <input type="password" id="profile-input-password" class="pfx-input" placeholder="Mínimo 10 caracteres" minlength="10" autocomplete="new-password" required
                                        ${UIEvents.attr("input", function(event,captured){ProfileView.updateStrength(this.value);}, [])}></label>
                                <div class="pfx-meter" aria-hidden="true"><span></span><span></span><span></span><span></span></div>
                                <span class="pfx-strength" id="profile-password-strength">Escribe la nueva contraseña</span>
                                <ul class="pfx-rules" id="profile-password-rules">
                                    <li data-rule="len">○ Al menos 10 caracteres</li>
                                    <li data-rule="case">○ Mayúsculas y minúsculas</li>
                                    <li data-rule="digit">○ Un número o un símbolo</li>
                                </ul>
                                <label class="pfx-field" for="profile-input-confirm"><span>Repite la nueva</span>
                                    <input type="password" id="profile-input-confirm" class="pfx-input" minlength="10" autocomplete="new-password" required></label>
                                <p id="profile-password-feedback" class="pfx-feedback" role="status" aria-live="polite" hidden></p>
                                <button type="submit" class="pfx-btn"><i class="fas fa-shield-halved" aria-hidden="true"></i> Cambiar contraseña</button>
                            </form>
                        </div>
                    </section>
                </div>
            </div>
        `;
    },

    // Strength of the new password while typing (only a guide; the server asks for 10+).
    updateStrength(value) {
        const pw = String(value || '');
        const rules = { len: pw.length >= 10, case: /[A-ZÁÉÍÓÚÑ]/.test(pw) && /[a-záéíóúñ]/.test(pw), digit: /\d|[^\w\s]/.test(pw) };
        const score = Math.min(4, (rules.len ? 1 : 0) + (rules.case ? 1 : 0) + (/\d/.test(pw) ? 1 : 0) + (/[^\w\s]/.test(pw) ? 1 : 0) + (pw.length >= 14 ? 1 : 0));
        const meter = document.querySelector('.pfx-meter');
        if (meter) { meter.dataset.score = String(pw ? score : 0); }
        const text = document.getElementById('profile-password-strength');
        if (text) {
            const words = ['Muy débil', 'Débil', 'Mejorable', 'Buena', 'Muy buena'];
            text.textContent = pw ? words[score] + (pw.length < 10 ? ' · aún no vale (mínimo 10)' : '') : 'Escribe la nueva contraseña';
            text.dataset.ok = String(pw.length >= 10);
        }
        document.querySelectorAll('#profile-password-rules li').forEach(li => {
            const ok = !!rules[li.dataset.rule];
            li.classList.toggle('is-ok', ok);
            li.textContent = (ok ? '✓ ' : '○ ') + li.textContent.replace(/^[✓○] /, '');
        });
    },

    // The week's classes may not be loaded yet: ask for them and redraw the figures.
    refreshStats() {
        const box = document.getElementById('profile-stats');
        const user = AuthManager.getCurrentUser();
        if (!box || !user) return;
        SafeHTML.set(box, this._stats(user).map(s => `<button type="button" class="pfx-stat" ${UIEvents.attr("click", function(event,captured){App.navigate(String(captured[0]));}, [s.route])}><b>${Utils.sanitizeHTML(String(s.n))}</b><span>${Utils.sanitizeHTML(s.label)}</span></button>`).join(''));
    },

    async init() {
        const FS = this._get('FileStorage'), user = AuthManager.getCurrentUser();
        if (FS?.needClasses && user && (user.professorId || user.role === 'student')) {
            const week = this._week();
            FS.needClasses(this._iso(week[0]), this._iso(week[6]), () => this.refreshStats());
        }
        const form = document.getElementById('profile-email-form');
        if (!form) return;
        const epoch = AuthManager._epoch;
        const feedback = document.getElementById('profile-email-feedback');
        const retry = document.getElementById('profile-email-retry');
        retry.hidden = true;
        try {
            const response = await fetch('/api/account/email', { cache: 'no-store', signal: AbortSignal.timeout(15000) });
            const result = await response.json();
            if (epoch !== AuthManager._epoch || !form.isConnected) return;
            if (!response.ok || !result.success) throw new Error('No se pudo cargar');
            form.querySelector('#profile-email-input').value = result.email;
            form.querySelector('#profile-email-input').disabled = false;
            form.querySelector('button').disabled = false;
            document.getElementById('profile-email-current').textContent = result.email || 'No hay correo electrónico registrado.';
            const heroEmail = document.getElementById('profile-hero-email');
            if (heroEmail && result.email) { heroEmail.textContent = result.email; heroEmail.hidden = false; }
            feedback.textContent = '';
        } catch {
            if (epoch !== AuthManager._epoch || !form.isConnected) return;
            feedback.textContent = 'No se pudo cargar la configuración. Vuelve a intentarlo.';
            retry.hidden = false;
        }
    },
    async handleAccountEmail(event) {
        event.preventDefault();
        if (this._savingProfile) return;
        const form = event.target;
        const feedback = document.getElementById('profile-email-feedback');
        const email = form.querySelector('#profile-email-input').value.trim();
        const password = form.querySelector('#profile-email-password');
        const button = form.querySelector('button');
        const epoch = AuthManager._epoch;
        this._savingProfile = true;
        button.disabled = true;
        feedback.textContent = 'Verificando y guardando…';
        try {
            const response = await fetch('/api/account/email', {
                method: 'PUT', headers: { 'Content-Type': 'application/json', 'X-SICAF-CSRF': AuthManager.getCsrfToken() },
                signal: AbortSignal.timeout(15000), body: JSON.stringify({ email, currentPassword: password.value })
            });
            const result = await response.json();
            if (epoch !== AuthManager._epoch || !form.isConnected) return;
            feedback.textContent = response.ok && result.success ? 'Correo electrónico actualizado.' : result.error || 'No se pudo guardar. Vuelve a intentarlo.';
            if (response.ok && result.success) {
                AuthManager.storeSession(result);
                document.getElementById('profile-email-current').textContent = email.toLowerCase();
                form.closest('details').open = false;
            }
        } catch {
            if (epoch === AuthManager._epoch && form.isConnected) feedback.textContent = 'No se pudo confirmar el guardado. Recarga para comprobar el correo configurado.';
        } finally {
            password.value = '';
            this._savingProfile = false;
            button.disabled = false;
        }
    },
    async handleAvatarUpload(event) {
        const picked = event.target.files[0];
        if (!picked || this._savingProfile) return;
        if (!['image/png','image/jpeg','image/gif','image/webp'].includes(picked.type) || picked.size > 10 * 1024 * 1024) {
            Utils.showToast('Selecciona una imagen PNG, JPEG, GIF o WebP de hasta 10 MB.', 'error');
            event.target.value = '';
            return;
        }
        this._savingProfile = true;
        const epoch = AuthManager._epoch;
        event.target.disabled = true;
        try {
            const file = await PhotoCropper.open(picked);
            if (!file) return;
            const body = new FormData();
            body.append('photo', file);
            const response = await fetch('/api/users/me/photo', {method:'POST',body});
            const result = await response.json();
            if (epoch !== AuthManager._epoch) return;
            if (!response.ok || !result.success || !result.user) throw new Error(result.error || 'No se pudo guardar la foto.');
            AuthManager.storeSession(result);
            App.setupHeaderInfo();
            App.setupUserInfo();
            Utils.showToast('Foto de perfil actualizada correctamente', 'success');
            App.render();
        } catch (error) {
            if (epoch === AuthManager._epoch) Utils.showToast(error.message || 'No se pudo guardar la foto.', 'error');
        } finally {
            this._savingProfile = false;
            event.target.disabled = false;
            event.target.value = '';
        }
    },

    async handleUpdateDetails(event) {
        event.preventDefault();
        if (this._savingProfile) return;
        const name = document.getElementById('profile-input-name').value.trim();
        const username = document.getElementById('profile-input-username').value.trim();

        if (!name || !username) {
            Utils.showToast('El nombre y el nombre de usuario son obligatorios', 'warning');
            return;
        }

        this._savingProfile = true;
        const epoch = AuthManager._epoch;
        try {
            const user = AuthManager.getCurrentUser();
            const token = AuthManager.getCsrfToken();

            const response = await fetch(`/api/users/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify({ name, username })
            });

            const result = await response.json();
            if (epoch !== AuthManager._epoch) return;
            if (response.ok && result.success && result.user) {
                AuthManager.storeSession(result);
                
                // Update the app sidebar/header displays
                App.setupUserInfo();
                App.setupHeaderInfo();
                
                Utils.showToast('Perfil actualizado correctamente', 'success');
                App.render();
            } else {
                Utils.showToast(result.error || 'Error al actualizar perfil', 'error');
            }
        } catch (error) {
            console.error('Error al actualizar datos:', error);
            if (epoch === AuthManager._epoch) Utils.showToast('Error de conexión con el servidor', 'error');
        } finally { this._savingProfile = false; }
    },

    async handleChangePassword(event) {
        event.preventDefault();
        if (this._savingProfile) return;
        const currentPasswordInput = document.getElementById('profile-input-current-password');
        const currentPassword = currentPasswordInput.value;
        const feedback = document.getElementById('profile-password-feedback');
        const button = event.target.querySelector('button[type="submit"]');
        const announce = message => { feedback.textContent = message; feedback.hidden = false; };
        const password = document.getElementById('profile-input-password').value;
        const confirm = document.getElementById('profile-input-confirm').value;

        if (!currentPassword) {
            announce('Introduce tu contraseña actual para cambiarla.');
            currentPasswordInput.focus();
            return;
        }
        if (password.length < 10) {
            announce('La contraseña debe tener al menos 10 caracteres.');
            return;
        }

        if (password !== confirm) {
            announce('Las contraseñas no coinciden. Revisa la confirmación.');
            return;
        }

        this._savingProfile = true;
        button.disabled = true;
        announce('Verificando y guardando la nueva contraseña…');
        const epoch = AuthManager._epoch;
        try {
            const user = AuthManager.getCurrentUser();
            const token = AuthManager.getCsrfToken();

            const response = await fetch(`/api/users/${user.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'X-SICAF-CSRF': token
                },
                body: JSON.stringify({ password, currentPassword })
            });

            const result = await response.json();
            if (epoch !== AuthManager._epoch) return;
            if (response.ok && result.success && result.user) {
                AuthManager.storeSession(result);
                currentPasswordInput.value = '';
                document.getElementById('profile-input-password').value = '';
                document.getElementById('profile-input-confirm').value = '';
                announce('Contraseña cambiada. Las sesiones anteriores ya no son válidas.');
            } else {
                announce(result.error || 'No se pudo cambiar la contraseña. Reinténtalo.');
            }
        } catch (error) {
            console.error('Error al cambiar contraseña:', error);
            if (epoch === AuthManager._epoch) announce('No se pudo conectar con el servidor. Comprueba tu conexión y reinténtalo.');
        } finally { this._savingProfile = false; button.disabled = false; }
    }
};

window.ProfileView = ProfileView;
